'use client';

import type { PrV2CommentTarget } from '@/lib/pr-v2/tabs';
import { MessageSquare } from 'lucide-react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useEffect, useRef } from 'react';

import { PR_V2_TOOLBAR_ICON_BUTTON_CLASSNAME } from '../shared/pr-v2-toolbar-styles';
import { PrV2CommentsPanel } from './pr-v2-comments-panel';
import { usePrV2CommentCommands } from './use-pr-v2-comment-commands';
import { usePrV2CommentThreads } from './use-pr-v2-comment-threads';
import { usePrV2FixAll } from './use-pr-v2-fix-all';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatCountedNoun } from '@/lib/auto-fix-loop/outcome-copy';
import { PR_V2_STICKY_TABS_LIST_SELECTOR } from '@/lib/pr-v2/sticky-tabs-offset';
import { usePrV2CommentsPopoverStore } from '@/lib/stores/pr-v2-comments-popover-store';
import { cn } from '@/lib/utils';

// Sticky tab bar buttons can scroll the PR container when the browser focuses them.
function preventMouseFocus(event: ReactMouseEvent<HTMLButtonElement>) {
  event.preventDefault();
}

type PrV2CommentsControlsProps = {
  owner: string;
  repo: string;
  number: string;
  /** Switches to the comment's tab and opens it there. */
  onOpenComment: (target: PrV2CommentTarget) => void;
};

/**
 * The review tab bar's comments control: the number of open review comments, which opens the
 * comments popover.
 */
export function PrV2CommentsControls({
  owner,
  repo,
  number,
  onOpenComment,
}: PrV2CommentsControlsProps) {
  const threads = usePrV2CommentThreads({ owner, repo, number });
  const fixAll = usePrV2FixAll({ owner, repo, number, threads });
  const isPopoverOpen = usePrV2CommentsPopoverStore((state) => state.isOpen);
  const setPopoverOpen = usePrV2CommentsPopoverStore((state) => state.setOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Set when a click or focus outside the popover closes it, so focus stays where it went.
  const interactedOutsideRef = useRef(false);
  const openCount = threads.openBotCount + threads.openHumanCount;

  useEffect(
    () => () => {
      // Leaving the pull request unmounts these controls. Close the popover so the next pull
      // request's page doesn't open with it.
      setPopoverOpen(false);
    },
    [setPopoverOpen],
  );

  useEffect(() => {
    // Opened from another part of the page while the tab bar is scrolled out of view:
    // bring it to the top so the popover has room to open below it.
    const trigger = triggerRef.current;
    if (!isPopoverOpen || !trigger) {
      return;
    }

    const { top, bottom } = trigger.getBoundingClientRect();
    if (top < 0 || bottom > window.innerHeight) {
      (trigger.closest<HTMLElement>(PR_V2_STICKY_TABS_LIST_SELECTOR) ?? trigger).scrollIntoView({
        block: 'start',
      });
    }
  }, [isPopoverOpen]);

  function goToComment(commentId: string) {
    setPopoverOpen(false);
    onOpenComment(threads.getCommentTarget(commentId));
  }

  usePrV2CommentCommands({ threads, goToComment, fixAll });

  return (
    <Popover open={isPopoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          data-testid="pr-v2-comments-popover-trigger"
          aria-label={openCount > 0 ? formatCountedNoun(openCount, 'open comment') : 'Comments'}
          className={cn(
            PR_V2_TOOLBAR_ICON_BUTTON_CLASSNAME,
            'px-1 data-[state=open]:bg-zinc-900 data-[state=open]:text-zinc-100',
          )}
          onMouseDown={preventMouseFocus}
        >
          <MessageSquare className="size-3.5" />
          {openCount > 0 ? (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-zinc-800 px-1 text-[10px] font-medium text-zinc-200 tabular-nums">
              {openCount}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="pr-v2-comments-popover"
        align="end"
        sideOffset={6}
        collisionPadding={12}
        className="flex max-h-[min(36rem,var(--radix-popover-content-available-height))] w-[min(23rem,calc(100vw-24px))] flex-col overflow-hidden p-0"
        onInteractOutside={() => {
          interactedOutsideRef.current = true;
        }}
        onCloseAutoFocus={(event) => {
          // Return focus without letting the browser scroll the PR back to the sticky tab bar. Like
          // Radix, leave it where a click or focus outside the popover put it.
          event.preventDefault();
          if (!interactedOutsideRef.current) {
            triggerRef.current?.focus({ preventScroll: true });
          }
          interactedOutsideRef.current = false;
        }}
      >
        <PrV2CommentsPanel
          owner={owner}
          repo={repo}
          number={number}
          threads={threads}
          fixAll={fixAll}
          onOpenThread={goToComment}
        />
      </PopoverContent>
    </Popover>
  );
}
